import { useEffect } from "preact/hooks";
import { Button, countLabel, DataTable, DetailLayout, EmptyState, FigureStrip, fmtNum, List, ListRow, PageHeader, RailPanel, SampleMark, StatusBadge } from "../../../../ui/src/index.ts";
import type { FeedbackTone, TableColumn } from "../../../../ui/src/index.ts";
import { percentile } from "../../domain/activity.ts";
import { perMillion, usd } from "../../domain/money.ts";
import { bestPrice, pricingRows, ROOM_MIN_ANSWERS, type PricingRow, type PricingSignal } from "../../domain/pricing.ts";
import { presetAsks } from "../../domain/golden.ts";
import type { HubSummary } from "../../domain/types.ts";
import { loadEvents, loadGolden } from "../actions.ts";
import { HeaderActions } from "../Guide.tsx";
import { Failed, Loading, Section } from "../parts.tsx";
import { href } from "../router.ts";
import { useHubLabel } from "../hubData.ts";
import { useStore } from "../store.ts";
import { capitalize } from "../words.ts";

const SIGNAL_WORDS = { pricedOut: "Priced out", preset: "Preset bids", room: "Room to raise", holds: "Holds", quiet: "No asks" } satisfies Record<PricingSignal, string>;
const SIGNAL_TONES = { pricedOut: "info", preset: "neutral", room: "success", holds: "neutral", quiet: "neutral" } satisfies Record<PricingSignal, FeedbackTone>;

export function Pricing({ hub }: { hub: HubSummary }) {
  const short = useHubLabel(hub.hub.name);
  const events = useStore((s) => s.events[hub.hub.id]);
  const sample = useStore((s) => s.sample);
  const golden = useStore((s) => s.golden);
  useEffect(() => { void loadEvents(hub.hub.id); void loadGolden(); }, [hub.hub.id]);

  const header = <PageHeader title={<>Price your questions <strong>to win</strong></>}
    description="Where your price turned asks away, and where consumers bid well above it. Studio recommends a price; it never changes one."
    action={<HeaderActions />} />;
  const label = (id: string) => capitalize(hub.manifest.intents.find((i) => i.id === id)?.label ?? id);
  const floor = sample.deliveryFeePerMillion + sample.inputsPerMillion;
  const rail = <RailPanel title="Fee and inputs">
    <dl class="st-facts"><div><dt>Per million answers<SampleMark /></dt><dd>{perMillion(floor)}</dd></div></dl>
    <p class="st-rail-note">Studio never recommends a price at or below this.</p>
  </RailPanel>;

  if (!events || events.status === "loading") return <>{header}<DetailLayout rail={rail}><Loading label="Loading events" /></DetailLayout></>;
  if (events.status === "failed") return <>{header}<DetailLayout rail={rail}><Failed what="the events" error={events.error} retry={() => void loadEvents(hub.hub.id, true)} /></DetailLayout></>;

  const presets = presetAsks(hub, golden?.status === "ok" ? golden.value.questions : []);
  const rows = pricingRows(hub, events.value.events, presets);
  const setAside = rows.reduce((n, r) => n + r.setAside, 0);
  const lost = (r: PricingRow) => r.pricedOutBids.length;
  const lostAsks = rows.reduce((n, r) => n + lost(r), 0);
  const lostQuestions = rows.filter((r) => lost(r) > 0).length;
  const lostUsd = rows.reduce((n, r) => n + r.pricedOutBids.reduce((m, b) => m + b, 0), 0) / 1e6;
  const ratios = rows.flatMap((r) => r.answeredBids.map((b) => b / r.askPerMillion));
  const medianRatio = percentile(ratios, 50);

  const question = (id: string) => href({ name: "question", hub: hub.hub.id, intent: id });
  // Only a price that would have earned more in the same events, best gain first; then priced-out questions to keep as they are.
  const scored = rows.map((r) => ({ r, best: bestPrice(r.answeredBids, r.pricedOutBids, r.askPerMillion, floor) }));
  // A raise needs a few answers behind it; one or two bids are too thin to act on.
  const changes = scored.filter((x) => x.best && (x.best.direction === "lower" || (x.r.answered >= ROOM_MIN_ANSWERS && !x.r.uniformBids))).sort((a, b) => (b.best!.earnedPerMillion - b.best!.todayPerMillion) - (a.best!.earnedPerMillion - a.best!.todayPerMillion));
  const keeps = scored.filter((x) => !x.best && x.r.signal === "pricedOut");
  const advice = [
    ...changes.map(({ r, best }) => <ListRow key={r.intent} name={`${best!.direction === "lower" ? "Lower" : "Raise"} “${label(r.intent)}” to ${perMillion(best!.price)}`}
      sub={`At ${perMillion(best!.price)} the newest events would have earned ${usd(best!.earnedPerMillion / 1e6)} from ${countLabel(best!.answers, "answer")}, against ${usd(best!.todayPerMillion / 1e6)} at ${perMillion(r.askPerMillion)}.`}
      aside={<Button size="sm" href={question(r.intent)}>Open question</Button>} />),
    ...keeps.map(({ r }) => <ListRow key={r.intent} name={`Keep “${label(r.intent)}” at ${perMillion(r.askPerMillion)}`}
      sub={`${countLabel(lost(r), "priced-out ask")} bid less, but a lower price would earn less from the answers it already gets.`}
      aside={<Button size="sm" href={question(r.intent)}>Open question</Button>} />),
  ];

  const columns: TableColumn<PricingRow>[] = [
    { id: "question", label: "Question", fill: true, resizable: true, render: (r) => <span class="st-q"><a href={question(r.intent)} title={label(r.intent)}>{label(r.intent)}</a></span> },
    { id: "price", label: "Price", numeric: true, render: (r) => perMillion(r.askPerMillion) },
    { id: "answered", label: "Answered", numeric: true, render: (r) => fmtNum(r.answered) },
    { id: "setAside", label: "Set aside", numeric: true, render: (r) => (r.setAside ? <span class="st-note">{fmtNum(r.setAside)}</span> : "–") },
    { id: "bid", label: "Median bid", numeric: true, render: (r) => (r.medianAnsweredBid === null ? "–" : perMillion(r.medianAnsweredBid)) },
    { id: "lost", label: "Priced out", numeric: true, render: (r) => <>{fmtNum(lost(r))}{r.retried > 0 && <span class="st-note"> · {fmtNum(r.retried)} retried</span>}</> },
    { id: "signal", label: "Signal", render: (r) => <span title={r.signal === "preset" ? "Most answered asks bid exactly one amount, as a preset bid would" : undefined}><StatusBadge tone={SIGNAL_TONES[r.signal]}>{SIGNAL_WORDS[r.signal]}</StatusBadge></span> },
  ];

  return <>
    {header}
    <DetailLayout rail={rail}>
      <div class="st-summary" data-tour="summary"><FigureStrip label="Pricing in the newest events" figures={[
        { label: "Priced out", value: fmtNum(lostAsks), context: `${countLabel(lostQuestions, "question")} · newest events` },
        { label: "Bids turned away", value: usd(lostUsd), context: "what the priced-out asks offered" },
        { label: "Bids on answered asks", value: medianRatio === null ? "–" : `${medianRatio.toFixed(1)}×`, context: "the price, at the median" },
      ]} /></div>
      {setAside > 0 && <p class="st-rail-note st-setaside">{countLabel(setAside, "ask")} set aside: they repeat the hub's examples or golden questions, as the Console's examples and lessons do.</p>}
      <Section title="Recommendations" note="Only prices that would have earned more in the same events" tour="recommendations">
        {advice.length ? <List class="st-attention">{advice}</List> : <EmptyState>No pricing signals in the newest events.</EmptyState>}
      </Section>
      <Section title="By question" note={`From the last ${events.value.events.length} events`} tour="by-question">
        <DataTable caption={`Pricing signals for ${short}`} columns={columns} rows={rows} rowKey={(r) => r.intent} storageKey="studio-pricing" />
      </Section>
    </DetailLayout>
  </>;
}
